import type { ReactElement } from 'react';
import { useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    ChevronDown,
    ChevronRight,
    GripVertical,
    MoreVertical,
    Pencil,
    Plus,
    Trash2
} from 'lucide-react';

import type { MenuItemTypeTreeNode } from '@/lib/buildMenuItemTypeTree';

interface MenuItemTypeNodeProps {
    node: MenuItemTypeTreeNode;
    /** Уровень в дереве для расчёта отступа. depth из самой ноды используется
     * как fallback, если уровень не передан явно (для случаев вне dnd-контекста). */
    level?: number;
    /** Раскрыт ли узел (управляется родителем для удобства "развернуть все"). */
    expanded: boolean;
    onToggleExpanded: (id: string) => void;
    onEdit: (node: MenuItemTypeTreeNode) => void;
    onDelete: (node: MenuItemTypeTreeNode) => void;
    onAddChild: (node: MenuItemTypeTreeNode) => void;
    /** Включён ли режим drag-and-drop. */
    draggable?: boolean;
    children?: ReactElement | ReactElement[];
}

export function MenuItemTypeNode({
    node,
    level,
    expanded,
    onToggleExpanded,
    onEdit,
    onDelete,
    onAddChild,
    draggable = true,
    children
}: MenuItemTypeNodeProps): ReactElement {
    const [menuOpen, setMenuOpen] = useState(false);

    const sortable = useSortable({ id: node.id, disabled: !draggable });
    const droppable = useDroppable({
        id: `drop-onto-${node.id}`,
        data: { type: 'drop-onto', nodeId: node.id },
        disabled: !draggable
    });

    const style = {
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
        opacity: sortable.isDragging ? 0.4 : 1
    };

    const depthLevel = level ?? node.depth;
    const paddingLeft = `${depthLevel * 24}px`;

    // Стилизация по уровню — зеркало SpecializationNode.
    let nameClass = 'text-sm text-gray-700';
    if (depthLevel === 0) {
        nameClass = 'text-base font-semibold text-gray-900';
    } else if (node.children.length > 0) {
        nameClass = 'text-sm font-medium text-gray-800';
    }
    if (!node.isActive) {
        nameClass = 'text-sm italic text-gray-400';
    }

    const hasChildren = node.children.length > 0;

    // Возрастные бейджи (MF-5/MF-7 UI): различаем СОБСТВЕННОЕ ограничение
    // узла (isAgeRestricted + minAge) и УНАСЛЕДОВАННОЕ от предков
    // (effectiveMinAge — денорм, считает только сервер). Порог — число,
    // НЕ хардкод «18».
    const ownMinAge = node.isAgeRestricted ? (node.minAge ?? 0) : 0;
    const showOwnBadge = ownMinAge > 0;
    // Унаследованный бейдж: либо у узла нет собственного флага, но предок
    // ограничен, либо предок строже собственного порога.
    const inheritedMinAge = node.effectiveMinAge > ownMinAge ? node.effectiveMinAge : 0;
    const showInheritedBadge = inheritedMinAge > 0;

    const dropOntoClass = droppable.isOver
        ? 'bg-amber-50 border-amber-300 border-dashed'
        : 'border-transparent';

    return (
        <div ref={sortable.setNodeRef} style={style}>
            <div
                ref={droppable.setNodeRef}
                className={`flex items-center gap-2 border ${dropOntoClass} rounded px-1 py-1.5 transition hover:bg-gray-50`}
                style={{ paddingLeft }}
                data-testid={`menu-item-type-node-${node.id}`}
            >
                {draggable && (
                    <button
                        type="button"
                        {...sortable.attributes}
                        {...sortable.listeners}
                        aria-label="Перетащить"
                        className="flex cursor-grab items-center text-gray-400 transition hover:text-gray-700 active:cursor-grabbing"
                        data-testid={`menu-item-type-drag-handle-${node.id}`}
                    >
                        <GripVertical size={16} />
                    </button>
                )}

                <button
                    type="button"
                    onClick={() => onToggleExpanded(node.id)}
                    aria-label={expanded ? 'Свернуть' : 'Развернуть'}
                    className={`flex items-center text-gray-500 ${hasChildren ? 'hover:text-gray-900' : 'invisible'}`}
                    data-testid={`menu-item-type-expand-${node.id}`}
                >
                    {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>

                <span className={`flex-1 truncate ${nameClass}`}>
                    {node.name}
                    {showOwnBadge && (
                        <span
                            className="ml-2 inline-block rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-800"
                            title="Собственное возрастное ограничение узла"
                            data-testid={`menu-item-type-age-badge-${node.id}`}
                        >
                            {ownMinAge}+
                        </span>
                    )}
                    {showInheritedBadge && (
                        <span
                            className="ml-2 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800"
                            title="Возрастное ограничение унаследовано от родительского узла"
                            data-testid={`menu-item-type-inherited-age-badge-${node.id}`}
                        >
                            {inheritedMinAge}+ (унасл.)
                        </span>
                    )}
                    {!node.isActive && (
                        <span
                            className="ml-2 text-xs italic text-gray-400"
                            data-testid={`menu-item-type-archived-badge-${node.id}`}
                        >
                            (неактивен)
                        </span>
                    )}
                    <span className="ml-2 text-xs text-gray-400">
                        /{node.slug}
                        {node.usageCount > 0 && (
                            <span
                                title="Число групп кафе, использующих узел"
                                data-testid={`menu-item-type-usage-${node.id}`}
                            >
                                {` · групп: ${node.usageCount}`}
                            </span>
                        )}
                    </span>
                </span>

                <div className="relative">
                    <button
                        type="button"
                        onClick={() => setMenuOpen((v) => !v)}
                        aria-label="Меню действий"
                        className="rounded p-1 text-gray-500 transition hover:bg-gray-100"
                        data-testid={`menu-item-type-menu-${node.id}`}
                    >
                        <MoreVertical size={16} />
                    </button>
                    {menuOpen && (
                        <>
                            <button
                                type="button"
                                onClick={() => setMenuOpen(false)}
                                aria-label="Закрыть меню"
                                className="fixed inset-0 z-10 cursor-default"
                            />
                            <div
                                className="absolute right-0 top-full z-20 mt-1 w-44 overflow-hidden rounded border border-gray-200 bg-white shadow-lg"
                                data-testid={`menu-item-type-menu-popover-${node.id}`}
                            >
                                <button
                                    type="button"
                                    onClick={() => {
                                        setMenuOpen(false);
                                        onEdit(node);
                                    }}
                                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 transition hover:bg-gray-100"
                                    data-testid={`menu-item-type-menu-edit-${node.id}`}
                                >
                                    <Pencil size={14} /> Редактировать
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setMenuOpen(false);
                                        onAddChild(node);
                                    }}
                                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 transition hover:bg-gray-100"
                                    data-testid={`menu-item-type-menu-add-child-${node.id}`}
                                >
                                    <Plus size={14} /> Добавить дочерний
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setMenuOpen(false);
                                        onDelete(node);
                                    }}
                                    className="flex w-full items-center gap-2 border-t border-gray-100 px-3 py-2 text-left text-sm text-red-600 transition hover:bg-red-50"
                                    data-testid={`menu-item-type-menu-delete-${node.id}`}
                                >
                                    <Trash2 size={14} /> Деактивировать
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {expanded && children !== undefined && (
                <div data-testid={`menu-item-type-children-${node.id}`}>{children}</div>
            )}
        </div>
    );
}
